import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Button, Flex, Typography, VerifiedIcon } from '@iheartjane/reefer';
import { formatCurrency } from '@iheartjane/utils';

import { useKioskSpacing } from '@jane/kiosk/hooks';
import { SCREEN_NAMES, useCheckout } from '@jane/kiosk/providers';

import { ButtonWrapper } from '../Checkout/checkout.styles';
import {
  CheckoutFlowButton,
  SubmitCustomerInfoButton,
} from '../CustomerInfoFields/CustomerInfoFields';
import { PaymentOptionCard } from './PaymentOptionCard';

export const PaymentSelectionScreen = () => {
  const { defaultSpacing, isIpad, getKioskSpacing } = useKioskSpacing();
  const payByBankUI = useFlag(FLAGS.c3PayByBankUI);
  const trustedByUI = useFlag(FLAGS.c3JanePay200kExperiment);

  const {
    cart,
    bankAccounts,
    isOrderLoading,
    handleContinueToMFAClick,
    handleLinkBankClick,
    handleSubmitWithValidation,
    janePayCartLoading,
    janePayOrderTotal,
    janePayServiceFeeAmount,
    selectedPayment,
    setScreen,
    setSelectedPayment,
    store,
  } = useCheckout();

  const PAYMENT_OPTIONS = {
    link: payByBankUI ? 'Pay by bank' : 'Link your bank account',
    cash: 'Pay at cashier',
  };

  const whiteLabel = store?.jane_pay_integration?.white_label;
  const cashBack = cart?.brand_discounts_total;

  const selectionText =
    payByBankUI || !cashBack
      ? `Select your payment method. Pay${payByBankUI ? ' by bank' : ''} now with JanePay for a fast, secure checkout, or pay at the counter.`
      : `Select your payment method and place your order. Your estimated Jane
    Gold cash back of ${formatCurrency(
      cashBack
    )} will be deposited to your linked bank account in
    1-3 days.`;
  const hasBankAccount = bankAccounts?.length > 0;

  const showLinkBankButton = selectedPayment === 'link';
  const showContinueButton = bankAccounts.find(
    (account) => account.account_last_4 === selectedPayment
  );

  const showCashbackBadge = (cashBack ?? 0) > 0 && !hasBankAccount;

  const linkOptions = hasBankAccount
    ? bankAccounts.map((account) => ({
        id: account.account_last_4,
        label: account.name,
        paymentValue: account.account_last_4,
      }))
    : [
        {
          id: 'link',
          label: payByBankUI ? (
            trustedByUI ? (
              <Flex alignItems="center" gap={4}>
                <VerifiedIcon size="sm" color="grays-black" />
                <Typography variant="small-body-regular">
                  Trusted by 200k+ shoppers
                </Typography>
              </Flex>
            ) : null
          ) : (
            PAYMENT_OPTIONS.link
          ),
          paymentValue: 'link',
        },
      ];

  return (
    <Flex flexDirection="column">
      <Typography>{selectionText}</Typography>
      <Flex
        flexDirection="column"
        alignItems="center"
        gap={16}
        py={defaultSpacing}
        aria-label={`Payment options, selected option: ${
          selectedPayment === 'link' || selectedPayment === 'cash'
            ? PAYMENT_OPTIONS[selectedPayment]
            : `account number ending with ${selectedPayment}`
        }`}
      >
        <Typography variant="caps-semibold" textAlign="center">
          Select payment method
        </Typography>
        {linkOptions.map((option) => (
          <PaymentOptionCard
            key={option.id}
            ariaLabel={
              payByBankUI && hasBankAccount
                ? `Select ${PAYMENT_OPTIONS.link} with ${option.label as string}`
                : undefined
            }
            name={payByBankUI ? PAYMENT_OPTIONS.link : (option.label as string)}
            nameSubLabel={payByBankUI ? option.label : undefined}
            onClick={() => setSelectedPayment(option.paymentValue)}
            selected={selectedPayment === option.paymentValue}
            showJanePayLogo={
              payByBankUI
                ? !whiteLabel && janePayServiceFeeAmount === 0
                : !whiteLabel
            }
            total={
              !payByBankUI || janePayServiceFeeAmount > 0
                ? janePayOrderTotal
                : undefined
            }
            feeAmount={janePayServiceFeeAmount}
            cashBack={cashBack ?? 0}
            showCashbackBadge={showCashbackBadge}
          />
        ))}
        <PaymentOptionCard
          name={PAYMENT_OPTIONS.cash}
          onClick={() => setSelectedPayment('cash')}
          selected={selectedPayment === 'cash'}
          showDefaultFeeLabel
          total={
            !payByBankUI || janePayServiceFeeAmount > 0
              ? (cart?.order_total ?? 0)
              : undefined
          }
        />
        <Typography
          variant="small-body-regular"
          color="grays-mid"
          textAlign="center"
        >
          For your privacy, you will be signed out automatically after placing
          your order.
        </Typography>
      </Flex>

      <ButtonWrapper
        marginTop={defaultSpacing}
        gap={getKioskSpacing(24, 16)}
        isIpad={isIpad}
      >
        <Button
          label={
            isIpad ? (
              'Cancel'
            ) : (
              <Typography as="h3" variant="h4-semibold">
                Cancel
              </Typography>
            )
          }
          onClick={() => setScreen(SCREEN_NAMES.customerInfo)}
          size={isIpad ? 'small' : 'large'}
          full
          variant="tertiary"
          disabled={isOrderLoading}
        />

        {showLinkBankButton ? (
          <CheckoutFlowButton
            ariaLabel="link your bank account to pay"
            buttonLabel="Link bank"
            loading={isOrderLoading}
            size={isIpad ? 'small' : 'large'}
            onClick={handleLinkBankClick}
          />
        ) : showContinueButton ? (
          <CheckoutFlowButton
            ariaLabel="continue to authenticate your phone number and submit order"
            buttonLabel="Continue"
            loading={isOrderLoading || janePayCartLoading}
            size={isIpad ? 'small' : 'large'}
            onClick={handleContinueToMFAClick}
          />
        ) : (
          <SubmitCustomerInfoButton
            loading={isOrderLoading}
            size={isIpad ? 'small' : 'large'}
            onClick={handleSubmitWithValidation}
          />
        )}
      </ButtonWrapper>
    </Flex>
  );
};
